import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import { ScrollArea } from "@/components/core/scroller";
import { IconAsteriskSimple, IconCheck, IconFile, IconGripVertical, IconLoader2, IconPlus, IconTrash, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import { Fragment, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { type UseFormReturn, useFieldArray, useForm, useWatch } from "react-hook-form";
import TextareaAutosize from "react-textarea-autosize";
import { v4 as uuidv4 } from "uuid";

export type Schema = {
	name: string;
	label: string;
	className?: string;
	placeholder?: string;
	type?: string;
};

export type AcceptedFile = {
	file: File | null;
	preview: string | null;
	id?: string | null;
	name?: string | null;
	_id: string;
	parent_id?: string | null;
	_lft?: number | null;
	_rgt?: number | null;
	_depth: number;
	type: string | null;
	lastModified: number;
	size: number;
	[key: string]: any;
	files?: AcceptedFile[];
	stenography: File | null;
	affidavit: File | null;
};

type CaseFilingEvidenceListProps = {
	data?: any[];
	acceptedDataTypes?: string[];
	schema: Schema[];
	metadata: Record<string, string | number>;
	className?: string;
	savingProgress?: {
		current: number;
		total: number;
		message: string;
		currentItemId?: string;
		savedItems: Set<string>;
		failedItems: Set<string>;
	} | null;
	permanentItemStatus?: {
		savedItems: Set<string>;
		failedItems: Set<string>;
	};
};

const mapDataToAcceptedFiles = (data: any[], schema: Schema[]): AcceptedFile[] => {
	return data.map((item, index) => {
		const id = item.id ? String(item.id) : null;
		const base: AcceptedFile = {
			file: item.file ?? null,
			preview: item.preview || "",
			parent_id: item.parent_id || null,
			id,
			_id: item._id ?? `${Date.now()}-${Math.random()}-${index}`,
			name: item.name || item.file?.file_name,
			type: item.type ?? item.file?.mime_type,
			lastModified: item.created_at ? new Date(item.created_at).getTime() : Date.now(),
			size: item.size ?? item.file?.size,
			stenography: null,
			affidavit: null,
			_depth: 0,
		};

		schema.map((field) => {
			base[field.name] = item[field.name] !== undefined ? item[field.name] : "";
		});

		return base;
	});
};

function CaseFilingEvidenceItem({
	file,
	index,
	schema,
	form,
	savingProgress,
	permanentItemStatus,
	onRemove,
}: {
	file: AcceptedFile;
	index: number;
	schema: Schema[];
	form: UseFormReturn<{ files: AcceptedFile[] }>;
	savingProgress?: {
		current: number;
		total: number;
		message: string;
		currentItemId?: string;
		savedItems: Set<string>;
		failedItems: Set<string>;
	} | null;
	permanentItemStatus?: {
		savedItems: Set<string>;
		failedItems: Set<string>;
	};
	onRemove: (index: number) => void;
}) {
	// Get saving status for this item
	const itemId = file._id || file.id;
	const isSaving = savingProgress?.currentItemId === itemId;
	const isSaved = savingProgress?.savedItems?.has(itemId) || permanentItemStatus?.savedItems?.has(itemId);
	const isFailed = savingProgress?.failedItems?.has(itemId) || permanentItemStatus?.failedItems?.has(itemId);

	const handleRemove = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		const itemId = file._id || file.id || `index-${index}`;
		console.log("handleRemove clicked for index:", index, "itemId:", itemId);
		onRemove(index);
	};

	return (
		<div className="relative flex flex-col w-full gap-1 py-2">
			{(isSaving || isSaved || isFailed) && (
				<div
					className={`absolute -left-6 top-2 inline-flex items-center justify-center w-5 h-5 rounded-full ${
						isSaving ? "bg-blue-500 animate-pulse" : isSaved ? "bg-green-500" : isFailed ? "bg-red-500" : ""
					}`}
				>
					{isSaving && <IconLoader2 size={12} className="text-white animate-spin" />}
					{isSaved && <IconCheck size={12} className="text-white" />}
					{isFailed && <IconX size={12} className="text-white" />}
				</div>
			)}
			<div className="flex flex-row items-center gap-2 w-full justify-between">
				<div className="flex flex-row items-center gap-2">
					<button type="button" className="inline-flex shrink-0 text-foreground/50 hover:text-foreground bg-background border-2 border-background">
						<IconGripVertical size={16} stroke={2} />
					</button>
					<div className="inline-flex items-center gap-2">
						<div className="text-sm font-semibold text-foreground-dark">
							{file.file ? (
								<button
									className="cursor-pointer inline-flex flex-row items-center gap-1 p-1 pr-2 w-auto rounded-md whitespace-nowrap bg-blue-50 hover:bg-blue-100"
									type="button"
								>
									<IconFile size={18} stroke={2} className="inline-flex text-blue-600" />
									<span className="text-sm font-semibold text-blue-600">{file.name}</span>
								</button>
							) : (
								<span className="text-sm font-semibold text-foreground-dark">{file.exhibit || file.witness_name || "New Item"}</span>
							)}
						</div>
					</div>
				</div>
				<div className="flex items-center">
					<button
						type="button"
						onClick={handleRemove}
						className="inline-flex shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors duration-200"
						title="Remove item"
						disabled={isSaving}
					>
						<IconTrash size={14} stroke={2} />
					</button>
				</div>
			</div>
			<div className="flex flex-col w-full rounded-md border border-border/50 overflow-hidden mt-2">
				<table className="w-full text-sm">
					<tbody>
						{schema.map((field) => (
							<Fragment key={field.name}>
								<tr className="border-b border-border/50 last:border-b-0 bg-muted/40">
									<td className="w-full flex flex-row gap-1 items-center py-1 px-2 whitespace-nowrap">
										{field.label}
										{(field.name === "exhibit" || field.name === "description" || field.name === "witness_name") && (
											<span className="text-danger-600">
												<IconAsteriskSimple size={8} stroke={3} />
											</span>
										)}
									</td>
									<td className="w-full h-full p-0">
										{field.type === "textarea" ? (
											<TextareaAutosize
												maxRows={5}
												className="resize-none pt-1 text-sm text-gray-400 group-hover:text-gray-600 leading-tight !border-none !outline-none flex w-full text-gray-600 px-2 py-1 min-h-[18px] h-full text-sm text-foreground-dark/75 outline-none ring-0 focus:ring-0"
												maxLength={1000}
												placeholder={`Add ${field.label}`}
												defaultValue={file[field.name] as string | undefined}
												{...form.register(`files.${index}.${field.name}`, {
													required: field.name === "description",
												})}
											/>
										) : (
											<FormField
												control={form.control}
												name={`files.${index}.${field.name}`}
												render={({ field: formField }) => (
													<FormItem>
														<Input
															className="px-2 py-1 min-h-[18px] h-full text-sm text-foreground-dark/75 outline-none ring-0 focus:ring-0"
															maxLength={1000}
															placeholder={`Add ${field.label}`}
															defaultValue={formField.value}
															{...formField}
														/>
													</FormItem>
												)}
											/>
										)}
									</td>
								</tr>
							</Fragment>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

const CaseFilingEvidenceList = forwardRef<any, CaseFilingEvidenceListProps>(
	(
		{
			data,
			metadata,
			schema,
			acceptedDataTypes = [
				"application/pdf",
				"image/*",
				"video/mp4",
				"audio/*",
				"application/vnd.ms-excel",
				"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			],
			className,
			savingProgress,
			permanentItemStatus,
		},
		ref,
	) => {
		const form = useForm<{ files: AcceptedFile[] }>({
			defaultValues: { files: [] },
		});
		const { fields, append, remove } = useFieldArray({
			control: form.control,
			name: "files",
		});

		const files = useWatch({
			control: form.control,
			name: "files",
		});

		const hasReset = useRef(false);

		useEffect(() => {
			if (data && !hasReset.current) {
				const mappedFiles = mapDataToAcceptedFiles(data, schema);
				form.reset({ files: mappedFiles });
				hasReset.current = true;
			}
		}, [data, form.reset, schema]);

		// Watch for changes in the files array and clean up undefined values
		useEffect(() => {
			const validFiles = files?.filter((f) => f !== undefined && f !== null) || [];
			console.log("Files array changed:", files?.length, "valid files:", validFiles.length);
			console.log(
				"File IDs:",
				validFiles.map((f) => f._id || f.id),
			);

			// If we have undefined files, clean them up
			if (files && validFiles.length !== files.length) {
				console.log("Cleaning up undefined files");
				form.setValue("files", validFiles);
			}
		}, [files, form]);

		// Handle removing items and clearing any permanent status
		const handleRemoveItem = (index: number) => {
			const fileToRemove = files[index];
			if (fileToRemove) {
				const itemId = fileToRemove._id || fileToRemove.id || `index-${index}`;
				console.log("Removing item:", itemId, "at index:", index);
				console.log("Files before removal:", files.length);
				console.log("File at index:", fileToRemove);
			}

			try {
				// Instead of using remove(index), manually filter the array
				const newFiles = files.filter((_, i) => i !== index);
				console.log("New files array after filter:", newFiles.length);

				// Update the form with the filtered array
				form.setValue("files", newFiles);
				console.log("Form updated with new files array");
			} catch (error) {
				console.error("Error removing item:", error);
			}
		};

		const { getRootProps, getInputProps, open } = useDropzone({
			accept: acceptedDataTypes.reduce(
				(acc, mime) => {
					acc[mime] = [];
					return acc;
				},
				{} as Record<string, string[]>,
			),
			noClick: true,
			noKeyboard: true,
			onDrop: (droppedFiles) => {
				droppedFiles.map((file, index) => {
					const id = uuidv4();
					const fileData: AcceptedFile = {
						file,
						_id: id,
						id: null,
						parent_id: null,
						name: file.name,
						size: file.size,
						type: file.type,
						lastModified: file.lastModified,
						preview: URL.createObjectURL(file),
						stenography: null,
						affidavit: null,
						_depth: 0,
					};
					console.log("Adding new file with _id:", id);
					append(fileData);
					return fileData;
				});
			},
		});

		// Expose form methods via ref
		useImperativeHandle(ref, () => ({
			getValues: form.getValues,
			setValue: form.setValue,
			reset: form.reset,
			watch: form.watch,
		}));

		return (
			<Form {...form}>
				<form className="flex flex-col w-full h-full">
					<div className={className}>
						<ScrollArea className="flex flex-col w-full">
							<div className="flex flex-col w-full p-4">
								{data?.length === 0 && files.length === 0 ? (
									<div {...getRootProps()} className="dropzone inline-flex flex-col justify-center items-center w-full min-h-[120px]">
										<input {...getInputProps()} />
										<div className="inline-flex flex-col justify-center items-center gap-2">
											<p className="max-w-[320px] text-center text-sm text-foreground/50">
												Drag 'n' drop some files here, or click the button below to add manually.
											</p>
											<Button className="mt-2" type="button" onClick={open} size="sm">
												<IconPlus size={14} stroke={2} />
												<span>Add {metadata.evidence_type} Evidence</span>
											</Button>
										</div>
									</div>
								) : (
									<div {...getRootProps()} className="dropzone inline-flex flex-col justify-center items-center w-full mb-2">
										<input {...getInputProps()} />
										<div className="flex flex-row items-center justify-between w-full gap-4">
											<span className="text-sm font-semibold text-foreground-dark">{metadata?.evidence_type} Evidence List</span>
											<Button className="mt-2" type="button" onClick={open} size="sm">
												<IconPlus size={14} stroke={2} />
												<span>Add</span>
											</Button>
										</div>
									</div>
								)}
								<div className="flex flex-col w-full pl-3">
									<div className="flex flex-col w-full pl-8 border-l border-dashed border-border">
										{files?.map((file, originalIndex) => {
											if (!file || file === undefined || file === null) return null;
											return (
												<CaseFilingEvidenceItem
													key={`${file._id}-${originalIndex}`}
													file={file}
													index={originalIndex}
													schema={schema}
													form={form}
													savingProgress={savingProgress}
													permanentItemStatus={permanentItemStatus}
													onRemove={handleRemoveItem}
												/>
											);
										})}
									</div>
								</div>
							</div>
						</ScrollArea>
					</div>
				</form>
			</Form>
		);
	},
);

CaseFilingEvidenceList.displayName = "CaseFilingEvidenceList";

export default CaseFilingEvidenceList;
